/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import { useMaterialUIController, setTransparentNavbar, setMiniSidenav } from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const [openNotificationMenu, setOpenNotificationMenu] = useState(false);
  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState({ name: "-", email: "-", phone: "-" });
  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  useEffect(() => {
    const firstNonEmpty = (...values) =>
      values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");

    const parseJwtPayload = (token) => {
      if (!token || typeof token !== "string") return {};
      const parts = token.split(".");
      if (parts.length < 2) return {};

      try {
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const normalized = `${base64}${"=".repeat((4 - (base64.length % 4)) % 4)}`;
        return JSON.parse(atob(normalized));
      } catch (e) {
        return {};
      }
    };

    const token = localStorage.getItem("token");
    const claims = parseJwtPayload(token);

    const profile = {
      name:
        firstNonEmpty(
          claims?.name,
          claims?.Name,
          claims?.unique_name,
          claims?.username,
          claims?.userName,
          claims?.UserName
        ) || "-",
      email:
        firstNonEmpty(
          claims?.email,
          claims?.Email,
          claims?.upn,
          claims?.preferred_username,
          localStorage.getItem("email")
        ) || "-",
      phone:
        firstNonEmpty(
          claims?.phone_number,
          claims?.phone,
          claims?.PhoneNumber,
          claims?.phoneNo,
          localStorage.getItem("phoneNo"),
          localStorage.getItem("phoneNumber")
        ) || "-",
    };

    setLoggedInUser(profile);
  }, []);

  const handleOpenNotificationMenu = (event) => setOpenNotificationMenu(event.currentTarget);
  const handleCloseNotificationMenu = () => setOpenNotificationMenu(false);
  const handleOpenProfileMenu = (event) => setOpenProfileMenu(event.currentTarget);
  const handleCloseProfileMenu = () => setOpenProfileMenu(false);
  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("isTocAccepted");
    handleCloseProfileMenu();
    window.location.href = "/authentication/sign-in";
  };

  // Render the notifications menu
  const renderNotificationMenu = () => (
    <Menu
      anchorEl={openNotificationMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openNotificationMenu)}
      onClose={handleCloseNotificationMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );

  const renderProfileMenu = () => (
    <Menu
      anchorEl={openProfileMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(openProfileMenu)}
      onClose={handleCloseProfileMenu}
      PaperProps={{
        sx: {
          mt: 1.5,
          minWidth: 300,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 14px 34px rgba(15, 23, 42, 0.16)",
        },
      }}
    >
      <MDBox px={2.5} py={1} sx={{ backgroundColor: "#f8fafc" }}>
        <MDTypography
          variant="caption"
          color="text"
          fontWeight="regular"
          sx={{ letterSpacing: "0.04em" }}
        >
          PROFILE
        </MDTypography>
      </MDBox>
      <Divider />
      <MDBox px={2.5} py={1}>
        <MDTypography variant="caption" color="text" fontWeight="light">
          Name
        </MDTypography>
        <MDTypography variant="button" color="dark" display="block" fontWeight="bold" mt={0.5}>
          {loggedInUser.name || "-"}
        </MDTypography>
      </MDBox>
      <Divider />
      <MDBox px={2.5} py={1}>
        <MDTypography variant="caption" color="text" fontWeight="light">
          Contact
        </MDTypography>
        <MDTypography variant="button" color="dark" display="block" fontWeight="bold" mt={0.5}>
          {loggedInUser.email !== "-" ? loggedInUser.email : loggedInUser.phone}
        </MDTypography>
      </MDBox>
      <Divider />
      <MDBox px={2.5} py={1.25}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleLogout}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            color: "#fff",
            backgroundColor: "#d32f2f",
            "&:hover": {
              backgroundColor: "#b71c1c",
            },
          }}
        >
          Logout
        </Button>
      </MDBox>
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"}>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleOpenProfileMenu}
              >
                <Icon sx={iconsStyle}>account_circle</Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenNotificationMenu}
              >
                <Icon sx={iconsStyle}>notifications</Icon>
              </IconButton>
              {renderProfileMenu()}
              {renderNotificationMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
